import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, DotsSixVertical, Copy, Check } from "@phosphor-icons/react";

const STATUSES = ["Active", "On Hold", "Completed"];
const CAMPAIGN_TYPES = ["Budget Based", "Creator Based"];

export default function EditCampaignDialog({ open, onOpenChange, campaign, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    brand_name: "", campaign_name: "", no_of_creators: 0, objective: "", commercial: 0, additional_costs: 0, additional_costs_note: "", agency_fee_pct: 0, status: "Active", campaign_type: "Budget Based", start_date: "", end_date: "", notes: "", assigned_to: [], internal_sheets: [], client_sheets: [],
  });
  const [saving, setSaving] = useState(false);
  const [executives, setExecutives] = useState([]);

  useEffect(() => {
    if (open && campaign) {
      setForm({
        brand_name: campaign.brand_name || "",
        campaign_name: campaign.campaign_name || "",
        no_of_creators: campaign.no_of_creators || 0,
        objective: campaign.objective || "",
        commercial: campaign.commercial || 0,
        additional_costs: campaign.additional_costs || 0,
        additional_costs_note: campaign.additional_costs_note || "",
        agency_fee_pct: campaign.agency_fee_pct || 0,
        status: campaign.status || "Active",
        campaign_type: campaign.campaign_type || "Budget Based",
        start_date: campaign.start_date || "",
        end_date: campaign.end_date || "",
        notes: campaign.notes || "",
        assigned_to: campaign.assigned_to || [],
        internal_sheets: Array.isArray(campaign.internal_sheets) ? campaign.internal_sheets.map((s) => ({ label: s.label || "", url: s.url || "" })) : [],
        client_sheets: Array.isArray(campaign.client_sheets) ? campaign.client_sheets.map((s) => ({ label: s.label || "", url: s.url || "" })) : [],
      });
    }
  }, [open, campaign]);

  useEffect(() => {
    if (open && isAdmin) {
      api.get("/users", { params: { status: "approved" } })
        .then(({ data }) => setExecutives(data))
        .catch(() => setExecutives([]));
    }
  }, [open, isAdmin]);

  const toggleAssignee = (uid) => {
    setForm((f) => {
      const has = (f.assigned_to || []).includes(uid);
      return { ...f, assigned_to: has ? f.assigned_to.filter((x) => x !== uid) : [...(f.assigned_to || []), uid] };
    });
  };

  const addSheet = (key) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { label: "", url: "" }] }));
  const updateSheet = (key, idx, field, val) => setForm((f) => {
    const next = [...(f[key] || [])];
    next[idx] = { ...next[idx], [field]: val };
    return { ...f, [key]: next };
  });
  const removeSheet = (key, idx) => setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, i) => i !== idx) }));
  const reorderSheet = (key, from, to) => setForm((f) => {
    if (from === to || from < 0 || to < 0) return f;
    const next = [...(f[key] || [])];
    if (from >= next.length || to >= next.length) return f;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return { ...f, [key]: next };
  });

  const save = async () => {
    if (!form.brand_name.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      const cleanSheets = (arr) => (arr || [])
        .map((s) => ({ label: (s.label || "").trim(), url: (s.url || "").trim() }))
        .filter((s) => s.label || s.url);
      const payload = {
        ...form,
        commercial: parseFloat(form.commercial || 0) || 0,
        additional_costs: parseFloat(form.additional_costs || 0) || 0,
        agency_fee_pct: parseFloat(form.agency_fee_pct || 0) || 0,
        no_of_creators: parseInt(form.no_of_creators || 0) || 0,
        internal_sheets: cleanSheets(form.internal_sheets),
        client_sheets: cleanSheets(form.client_sheets),
      };
      if (!isAdmin) delete payload.assigned_to;
      const { data } = await api.put(`/campaigns/${campaign.id}`, payload);
      toast.success("Campaign updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-campaign-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit campaign</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Brand name</Label>
            <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="mt-1" data-testid="edit-brand-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Campaign name</Label>
              <Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} className="mt-1" data-testid="edit-campaign-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">No. of creators</Label>
              <Input type="number" value={form.no_of_creators} onChange={(e) => setForm({ ...form, no_of_creators: e.target.value })} className="mt-1" data-testid="edit-no-of-creators" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Objective</Label>
            <Textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} rows={2} className="mt-1" data-testid="edit-objective" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Start date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="edit-start-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">End date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="edit-end-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Commercial / budget (₹)</Label>
              <Input type="number" value={form.commercial} onChange={(e) => setForm({ ...form, commercial: e.target.value })} className="mt-1" data-testid="edit-commercial" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Agency fee (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.agency_fee_pct}
                onChange={(e) => setForm({ ...form, agency_fee_pct: e.target.value })}
                className="mt-1"
                data-testid="edit-agency-fee-pct"
                placeholder="0"
              />
              <div className="text-[11px] text-slate-500 mt-1">Applied on top of client cost. Amount = client cost × %.</div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Campaign type</Label>
            <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
              <SelectTrigger className="mt-1" data-testid="edit-campaign-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CAMPAIGN_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`edit-campaign-type-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              {form.campaign_type === "Creator Based"
                ? "Track per-creator internal + client cost and see margin per creator and the campaign total."
                : "Track a single commercial budget across the whole campaign."}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" data-testid="edit-notes" />
          </div>

          <SheetGroup
            title="Internal Sheets"
            testidPrefix="internal-sheet"
            items={form.internal_sheets}
            onAdd={() => addSheet("internal_sheets")}
            onChange={(i, k, v) => updateSheet("internal_sheets", i, k, v)}
            onRemove={(i) => removeSheet("internal_sheets", i)}
            onReorder={(from, to) => reorderSheet("internal_sheets", from, to)}
          />

          <SheetGroup
            title="Client Sheets"
            testidPrefix="client-sheet"
            items={form.client_sheets}
            onAdd={() => addSheet("client_sheets")}
            onChange={(i, k, v) => updateSheet("client_sheets", i, k, v)}
            onRemove={(i) => removeSheet("client_sheets", i)}
            onReorder={(from, to) => reorderSheet("client_sheets", from, to)}
          />

          {isAdmin && (
            <div className="pt-2 border-t border-slate-200">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Assign executives</Label>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 border border-slate-200 rounded-md p-2 bg-slate-50/50">
                {executives.length === 0 ? (
                  <div className="text-xs text-slate-500 p-2">No approved team members yet.</div>
                ) : executives.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white text-sm">
                    <Checkbox
                      checked={(form.assigned_to || []).includes(u.id)}
                      onCheckedChange={() => toggleAssignee(u.id)}
                      data-testid={`assign-${u.id}`}
                    />
                    <span className="flex-1">
                      <span className="text-slate-900">{u.name}</span>
                      <span className="text-slate-500 text-xs ml-2">{u.email}</span>
                      {u.role === "admin" && <span className="text-[10px] uppercase tracking-wider text-blue-600 ml-2">admin</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">Anyone assigned — admin or executive — will see this campaign.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SheetGroup({ title, testidPrefix, items, onAdd, onChange, onRemove, onReorder }) {
  const list = items || [];
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch { /* noop */ }
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx ?? Number(e.dataTransfer.getData("text/plain"));
    if (Number.isInteger(from) && from !== i) onReorder?.(from, i);
    setDragIdx(null); setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div className="pt-2 border-t border-slate-200" data-testid={`${testidPrefix}-group`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{title}</Label>
        <Button
          type="button"
          variant="outline"
          onClick={onAdd}
          className="h-7 px-2 text-xs border-slate-300"
          data-testid={`${testidPrefix}-add-btn`}
        >
          <Plus size={12} className="mr-1" /> Add {title.replace(/s$/, "")}
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="text-[11px] text-slate-400 mt-1.5">No {title.toLowerCase()} added yet.</div>
      ) : (
        <div className="mt-2 space-y-2">
          {list.map((row, i) => (
            <div
              key={i}
              onDragOver={onDragOver(i)}
              onDrop={onDrop(i)}
              className={`flex items-center gap-2 rounded-md ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-blue-300 bg-blue-50/40" : ""} ${dragIdx === i ? "opacity-50" : ""}`}
              data-testid={`${testidPrefix}-row-${i}`}
            >
              <span
                draggable
                onDragStart={onDragStart(i)}
                onDragEnd={onDragEnd}
                className="p-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0"
                title="Drag to reorder"
                data-testid={`${testidPrefix}-drag-${i}`}
              >
                <DotsSixVertical size={16} />
              </span>
              <Input
                value={row.label || ""}
                onChange={(e) => onChange(i, "label", e.target.value)}
                placeholder="Label"
                className="w-40 shrink-0"
                data-testid={`${testidPrefix}-label-${i}`}
              />
              <Input
                value={row.url || ""}
                onChange={(e) => onChange(i, "url", e.target.value)}
                placeholder="https://…"
                className="flex-1"
                data-testid={`${testidPrefix}-url-${i}`}
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded shrink-0"
                title="Remove"
                data-testid={`${testidPrefix}-remove-${i}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

